import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Spin, Empty, message } from "antd";
import { RightOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { getDetectionsRange, getUnitNames } from "../../store";
import { LOGIN_PAGE } from "../../configuration/paths";
import { DateFilter } from "../common/DateFilter";
import { ExportButtons } from "../common/ExportButtons";
import { UnitsDrawer } from "../common/UnitsDrawer";
import { exportSummaryPdf, exportSummaryPptx } from "./exports";
import "./styles/alarm_summary.scss";

// Option A summary: total + alert-type bars + hazard pills + per-unit list.
// Computed live from the detections (get_detections) for the selected period, so it
// always reflects real alerts — the precomputed summary-reports table is empty for
// many units. One responsive component serves mobile and desktop.

// Same rolling presets as Alerts and Metrics for a uniform filter bar.
const PERIODS = ["Past 24 Hours", "Past Week", "Past Month", "Custom"];

// Display order + color for each raw trigger_type value.
const TYPE_META = [
  { key: "Automatic", label: "Automatic", color: "#0d47a1" },
  { key: "Manual", label: "Manual", color: "#4a90d9" },
  { key: "Tablet", label: "Tablet", color: "#90caf9" },
  { key: "FA/NM", label: "False Alarm / Near Miss", color: "#b0bec5" },
  { key: "None", label: "Near Miss", color: "#cfd8dc" },
  { key: "Debug", label: "Debug", color: "#78909c" },
];
const LEVELS = [
  { key: "0", label: "No Hazard", cls: "ok" },
  { key: "1", label: "Lights", cls: "warn" },
  { key: "2", label: "Sirens", cls: "err" },
];

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// [start, stop] Date pair for the chosen rolling period. Custom uses the explicit
// [fromStr, toStr] (whole-day inclusive); the presets count back from now.
const rangeFor = (period, custom) => {
  if (period === "Custom") {
    if (!custom || !custom[0] || !custom[1]) return null;
    return [new Date(`${custom[0]}T00:00:00`), new Date(`${custom[1]}T23:59:59`)];
  }
  const stop = new Date();
  const start = new Date(stop);
  if (period === "Past 24 Hours") start.setHours(start.getHours() - 24);
  else if (period === "Past Week") start.setDate(start.getDate() - 7);
  else start.setMonth(start.getMonth() - 1);   // Past Month
  return [start, stop];
};

const periodLabel = (period, custom) => {
  const r = rangeFor(period, custom);
  if (!r) return "Pick a start and end date";
  const [s, e] = r;
  const opt = { month: "short", day: "numeric", year: "numeric" };
  return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, opt)}`;
};

export const AlarmHistorySummary = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loggedIn = !!useSelector((state) => state.app.auth.token);
  const profile = useSelector((state) => state.app.profile);

  const [period, setPeriod] = useState("Past Week");
  const [custom, setCustom] = useState(null);   // [fromStr, toStr] for the Custom period
  const [loading, setLoading] = useState(true);
  const [dets, setDets] = useState([]);
  const [focusUnit, setFocusUnit] = useState(null);   // drill into a single unit's summary
  const [unitOptions, setUnitOptions] = useState([]); // [{name, serial}] for the Units drawer
  const [selectedUnits, setSelectedUnits] = useState([]); // [] = all units included
  const [drawerOpen, setDrawerOpen] = useState(false);
  const captureRef = useRef(null);

  useEffect(() => {
    if (!loggedIn) {
      navigate(LOGIN_PAGE);
      return;
    }
    const r = rangeFor(period, custom);
    if (!r) { setLoading(false); return; }   // Custom selected but no dates yet
    setLoading(true);
    const [s, e] = r;
    dispatch(
      getDetectionsRange({
        units: [],
        type: [],
        level: [],
        app_version: [],
        range: [s.toISOString().replace("Z", "+00:00"), e.toISOString().replace("Z", "+00:00")],
        sortBy: "-detection_date",
      })
    )
      .then((r2) => {
        setDets(Array.isArray(r2.payload) ? r2.payload : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [period, custom, loggedIn, dispatch, navigate]);

  // Unit list for the Units drawer (de-duped by serial, matching Metrics/Alerts).
  useEffect(() => {
    if (!loggedIn) return;
    dispatch(getUnitNames()).then((r) => {
      const seen = new Set();
      const opts = [];
      for (const p of r.payload || []) {
        if (!seen.has(p[1])) { seen.add(p[1]); opts.push({ name: p[0], serial: p[1] }); }
      }
      setUnitOptions(opts);
    });
  }, [loggedIn, dispatch]);

  const agg = useMemo(() => {
    const types = {};
    const levels = {};
    const units = {};
    // Restrict to the chosen units (empty selection = all), then optionally drill into one.
    const unitSet = selectedUnits.length ? new Set(selectedUnits) : null;
    const base = unitSet ? dets.filter((d) => unitSet.has(d.serial_number || d.vehicle_serial_number)) : dets;
    const view = focusUnit ? base.filter((d) => (d.serial_number || d.vehicle_serial_number) === focusUnit) : base;
    for (const d of view) {
      const t = d.trigger_type == null ? "None" : d.trigger_type;
      types[t] = (types[t] || 0) + 1;
      const l = String(d.trigger_level);
      levels[l] = (levels[l] || 0) + 1;
      const s = d.serial_number || d.vehicle_serial_number;
      if (!units[s]) units[s] = { name: d.vehicle_name || s, serial: s, count: 0, types: {}, levels: {} };
      units[s].count += 1;
      units[s].types[t] = (units[s].types[t] || 0) + 1;
      units[s].levels[l] = (units[s].levels[l] || 0) + 1;
    }
    const unitList = Object.values(units).sort((a, b) => b.count - a.count);
    const maxType = Math.max(1, ...TYPE_META.map((m) => types[m.key] || 0));
    return { total: view.length, types, levels, units: unitList, maxType };
  }, [dets, focusUnit, selectedUnits]);

  // Clicking a unit drills the summary into just that unit (stays on the page).
  const focusName = focusUnit ? (agg.units[0]?.name || focusUnit) : null;
  const openUnit = (serial) => {
    setFocusUnit(serial);
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  };

  const label = periodLabel(period, custom);
  const fileBase = `summary_${period.toLowerCase().replace(/ /g, "_")}_${iso(new Date())}`;
  const expMeta = () => ({
    subtitle: `${period}${focusName ? ` · ${focusName}` : ""} · ${label}`,
    company: profile?.company_name,
    user: profile?.username,
    date: new Date().toLocaleString(),
    file: fileBase,
  });

  const downloadCSV = () => {
    const cols = ["Unit", "Total", ...TYPE_META.map((m) => m.label), ...LEVELS.map((l) => l.label)];
    const rows = [cols.join(",")];
    for (const u of agg.units) {
      const row = [
        u.name,
        u.count,
        ...TYPE_META.map((m) => u.types[m.key] || 0),
        ...LEVELS.map((l) => u.levels[l.key] || 0),
      ];
      rows.push(row.join(","));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileBase}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    message.info("Downloaded the CSV summary");
  };

  const onPdf = () => exportSummaryPdf(captureRef.current, expMeta()).catch(() => message.error("PDF export failed"));
  const onPptx = () => exportSummaryPptx(captureRef.current, expMeta()).catch(() => message.error("PPTX export failed"));

  const typeRows = TYPE_META.filter((m) => (agg.types[m.key] || 0) > 0);
  const exportsDisabled = loading || !agg.total;

  return (
    <div className="rz-summary">
      <DateFilter
        options={PERIODS}
        value={period}
        onChange={setPeriod}
        onCustomRange={(f, t) => setCustom([f, t])}
        customNote="Custom ranges aren't precomputed — they may take a moment to generate."
        rightSlot={
          <>
            <button className="rz-units-btn" onClick={() => setDrawerOpen(true)}>
              ⚙ Units · {selectedUnits.length || "All"}
            </button>
            <ExportButtons onCsv={downloadCSV} onPdf={onPdf} onPptx={onPptx} disabled={exportsDisabled} />
          </>
        }
      />

      <div className="rz-sum-body">
      <div className="rz-sum-period">{label}</div>

      {focusUnit && (
        <button className="rz-sum-focus" onClick={() => setFocusUnit(null)}>
          <ArrowLeftOutlined /> All units · <b>{focusName}</b>
        </button>
      )}

      {loading ? (
        <div className="rz-sum-loading"><Spin /></div>
      ) : agg.total === 0 ? (
        <Empty className="rz-sum-empty" description={period === "Custom" && !custom ? "Pick a date range" : "No alerts in this period"} />
      ) : (
        <div className="rz-sum-grid" ref={captureRef}>
          <div className="rz-card rz-sum-total">
            <div className="rz-sum-lbl">Total alerts</div>
            <div className="rz-sum-big">{agg.total}</div>
          </div>

          <div className="rz-card rz-sum-types">
            <div className="rz-sum-lbl">Alert types</div>
            {typeRows.map((m) => {
              const n = agg.types[m.key] || 0;
              return (
                <div className="rz-sum-row" key={m.key}>
                  <span className="rz-sum-n">{m.label}</span>
                  <span className="rz-sum-track">
                    <span className="rz-sum-fill" style={{ width: `${(n / agg.maxType) * 100}%`, background: m.color }} />
                  </span>
                  <span className="rz-sum-v">{n}</span>
                </div>
              );
            })}
          </div>

          <div className="rz-card rz-sum-haz">
            <div className="rz-sum-lbl">Hazard level</div>
            <div className="rz-sum-pills">
              {LEVELS.map((l) => (
                <div className={`rz-sum-pill ${l.cls}`} key={l.key}>
                  <div className="rz-sum-pill-lbl">{l.label}</div>
                  <div className="rz-sum-pill-n">{agg.levels[l.key] || 0}</div>
                </div>
              ))}
            </div>
          </div>

          {!focusUnit && (
            <div className="rz-card rz-sum-units">
              <div className="rz-sum-lbl">Per unit</div>
              {agg.units.map((u) => (
                <button className="rz-sum-unit" key={u.serial} onClick={() => openUnit(u.serial)}>
                  <span className="rz-sum-unit-nm">{u.name}</span>
                  <span className="rz-sum-unit-r"><b>{u.count}</b><RightOutlined /></span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      </div>

      <UnitsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        units={unitOptions}
        value={{ units: selectedUnits }}
        onApply={(v) => setSelectedUnits(v.units)}
        title="Units in summary"
      />
    </div>
  );
};

export default AlarmHistorySummary;
